import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { getAllProducts, createProduct, deleteProduct, findAllCategory } from '../../api/productApi/ProductApi';
import { updateProduct } from '../../api/adminApi/AdminApi';
import { Plus, Search, Edit3, Trash2, Package, AlertCircle, CheckCircle2, Star, Image as ImageIcon } from 'lucide-react';

export default function ProductsAdmin() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);

    // States tìm kiếm và lọc danh mục giống CategoriesAdmin
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');

    const [currentPage, setCurrentPage] = useState(0);
    const pageSize = 10;

    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        price: '',
        stock: '',
        description: '',
        star: '5',
        categoryId: ''
    });

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const res = await getAllProducts(0, 1000);
            const innerData = res?.data || {};
            const rawList = innerData?.data || [];

            // Sắp xếp: Ưu tiên CÒN HÀNG lên đầu, sau đó đến sản phẩm mới nhất (ID giảm dần)
            const sortedProducts = rawList.sort((a, b) => {
                const isOutOfStockA = (a.stock <= 0 || a.status === 'OUT_OF_STOCK') ? 1 : 0;
                const isOutOfStockB = (b.stock <= 0 || b.status === 'OUT_OF_STOCK') ? 1 : 0;

                // Nếu trạng thái còn/hết hàng khác nhau, ưu tiên sản phẩm còn hàng (0 đứng trước 1)
                if (isOutOfStockA !== isOutOfStockB) {
                    return isOutOfStockA - isOutOfStockB;
                }

                // Nếu cùng trạng thái, ưu tiên sản phẩm mới thêm (ID lớn hơn lên trước)
                const idA = a.realId || a.id || 0;
                const idB = b.realId || b.id || 0;
                return idB - idA;
            });

            setProducts(sortedProducts);
        } catch (error) {
            console.error("Lỗi fetch sản phẩm:", error);
        } finally {
            setLoading(false);
        }
    };
    const fetchCategories = async () => {
        try {
            const res = await findAllCategory();
            const rawList = res?.data || res || [];

            let allCategories = [];
            rawList.forEach(parentCat => {
                if (parentCat.subCategories && parentCat.subCategories.length > 0) {
                    parentCat.subCategories.forEach(sub => {
                        allCategories.push({ id: sub.id, name: sub.name });
                    });
                } else {
                    allCategories.push({ id: parentCat.id, name: parentCat.name });
                }
            });
            setCategories(allCategories);
        } catch (error) {
            console.error("Lỗi lấy danh mục:", error);
        }
    };

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleImageFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleOpenCreateModal = () => {
        setFormData({ name: '', price: '', stock: '', description: '', star: '5', categoryId: '' });
        setSelectedFile(null);
        setPreviewUrl('');
        setIsModalOpen(true);
    };

    const handleEditClick = (item) => {
        setEditingProduct(item);
        let matchedCategoryId = item.categoryId || '';
        if (!matchedCategoryId && item.categoryName && categories.length > 0) {
            const foundCat = categories.find(c => c.name === item.categoryName);
            if (foundCat) matchedCategoryId = foundCat.id;
        }

        setFormData({
            name: item.name || '',
            price: item.price || 0,
            stock: item.stock || 0,
            description: item.description || '',
            star: item.star || '5',
            categoryId: matchedCategoryId
        });

        setSelectedFile(null);
        setPreviewUrl(item.thumbnail || '');
        setIsEditModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.categoryId) {
            Swal.fire({
                title: 'Chưa chọn danh mục!',
                text: 'Vui lòng chọn danh mục cho sản phẩm.',
                icon: 'warning',
                confirmButtonColor: '#e11d48',
                background: '#0f172a',
                color: '#ffffff'
            });
            return;
        }

        try {
            await createProduct(formData, selectedFile);
            Swal.fire({
                title: 'Thành công!',
                text: 'Thêm sản phẩm thành công!',
                icon: 'success',
                confirmButtonColor: '#4f46e5',
                background: '#0f172a',
                color: '#ffffff'
            });
            setIsModalOpen(false);
            fetchProducts();
        } catch (error) {
            Swal.fire({
                title: 'Thất bại!',
                text: error?.response?.data || 'Thêm sản phẩm thất bại!',
                icon: 'error',
                confirmButtonColor: '#e11d48',
                background: '#0f172a',
                color: '#ffffff'
            });
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        if (!editingProduct) return;

        try {
            const sendFormData = new FormData();
            const productDTO = {
                name: formData.name,
                price: parseFloat(formData.price) || 0,
                stock_quantity: parseInt(formData.stock) || 0,
                description: formData.description || "",
                star: parseFloat(formData.star) || 5,
                categoryId: formData.categoryId ? parseInt(formData.categoryId) : null
            };

            sendFormData.append("product", new Blob([JSON.stringify(productDTO)], { type: "application/json" }));
            if (selectedFile) sendFormData.append("file", selectedFile);

            const targetId = editingProduct.realId || editingProduct.id;
            await updateProduct(targetId, sendFormData);

            Swal.fire({
                title: 'Thành công!',
                text: 'Cập nhật sản phẩm thành công!',
                icon: 'success',
                confirmButtonColor: '#4f46e5',
                background: '#0f172a',
                color: '#ffffff'
            });
            setIsEditModalOpen(false);
            setEditingProduct(null);
            fetchProducts();
        } catch (error) {
            Swal.fire({
                title: 'Cập nhật thất bại!',
                text: error?.response?.data?.message || 'Cập nhật sản phẩm thất bại!',
                icon: 'error',
                confirmButtonColor: '#e11d48',
                background: '#0f172a',
                color: '#ffffff'
            });
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Xác nhận xóa?',
            text: "Bạn có chắc chắn muốn xóa sản phẩm này không?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#e11d48',
            cancelButtonColor: '#334155',
            confirmButtonText: 'Xóa ngay',
            cancelButtonText: 'Hủy',
            background: '#0f172a',
            color: '#ffffff'
        });

        if (result.isConfirmed) {
            try {
                await deleteProduct(id);
                Swal.fire({
                    title: 'Đã xóa!',
                    text: 'Sản phẩm đã được xóa thành công.',
                    icon: 'success',
                    confirmButtonColor: '#4f46e5',
                    background: '#0f172a',
                    color: '#ffffff'
                });
                fetchProducts();
            } catch (error) {
                Swal.fire({
                    title: 'Lỗi!',
                    text: 'Xóa sản phẩm thất bại!',
                    icon: 'error',
                    confirmButtonColor: '#e11d48',
                    background: '#0f172a',
                    color: '#ffffff'
                });
            }
        }
    };

    // Lọc sản phẩm theo từ khóa tên và danh mục giống CategoriesAdmin
    const filteredProducts = products.filter((item) => {
        const nameMatch = (item.name || '').toLowerCase().includes(searchTerm.toLowerCase());
        const descMatch = (item.description || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesSearch = nameMatch || descMatch;

        if (!matchesSearch) return false;

        if (selectedCategoryFilter !== 'all') {
            const itemCatId = String(item.categoryId || item.category?.id || '');
            const itemCatName = String(item.categoryName || item.category?.name || '').toLowerCase();
            const targetFilter = String(selectedCategoryFilter).toLowerCase();

            const matchId = itemCatId === targetFilter;
            const matchName = itemCatName.includes(targetFilter);
            if (!matchId && !matchName) return false;
        }

        return true;
    });

    // Tính toán phân trang dựa trên danh sách đã lọc
    const totalPages = Math.ceil(filteredProducts.length / pageSize);
    const paginatedProducts = filteredProducts.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

    // Reset về trang đầu tiên khi thay đổi tìm kiếm hoặc bộ lọc
    useEffect(() => {
        setCurrentPage(0);
    }, [searchTerm, selectedCategoryFilter]);

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header & Thống kê thu nhỏ */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-900/60 p-6 rounded-3xl border border-slate-800/80 backdrop-blur-xl">
                <div>
                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-indigo-400 bg-indigo-500/10 px-3 py-1 rounded-full border border-indigo-500/20">
                        Quản trị hệ thống
                    </span>
                    <h1 className="text-2xl sm:text-3xl font-extrabold font-serif text-white mt-2">
                        Quản Lý Sản Phẩm
                    </h1>
                </div>
                <button
                    onClick={handleOpenCreateModal}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-3 rounded-2xl text-xs font-bold transition shadow-lg shadow-indigo-600/25 flex items-center gap-2 cursor-pointer"
                >
                    <Plus className="w-4 h-4" /> Thêm sản phẩm mới
                </button>
            </div>

            {/* THANH TÌM KIẾM VÀ LỌC DANH MỤC GIỐNG CATEGORIES ADMIN */}
            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
                <div className="relative min-w-[280px] flex-1 max-w-md">
                    <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        placeholder="Tìm kiếm sản phẩm theo tên, mô tả..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-800 rounded-2xl pl-10 pr-4 py-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition shadow-inner"
                    />
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 font-bold shrink-0">Danh mục:</span>
                    <select
                        value={selectedCategoryFilter}
                        onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                        className="bg-slate-900 border border-slate-800 rounded-2xl px-4 py-3 text-xs text-slate-300 focus:outline-none focus:border-indigo-500 cursor-pointer"
                    >
                        <option value="all">Tất cả danh mục ({products.length})</option>
                        {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* BẢNG SẢN PHẨM HIỆN ĐẠI */}
            <div className="bg-slate-900 rounded-3xl border border-slate-800/80 overflow-hidden shadow-2xl">
                {loading ? (
                    <div className="p-12 text-center text-slate-400 text-xs">Đang tải dữ liệu sản phẩm...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs text-slate-300">
                            <thead className="bg-slate-950/60 text-slate-400 uppercase font-bold tracking-wider border-b border-slate-800 text-[11px]">
                                <tr>
                                    <th className="p-4 w-12 text-center">STT</th>
                                    <th className="p-4 w-20 text-center">Ảnh</th>
                                    <th className="p-4">Tên Sản Phẩm</th>
                                    <th className="p-4">Giá Bán</th>
                                    <th className="p-4 text-center">Tồn Kho</th>
                                    <th className="p-4 max-w-xs">Mô Tả</th>
                                    <th className="p-4">Danh Mục</th>
                                    <th className="p-4 text-center">Trạng Thái</th>
                                    <th className="p-4 text-center">Đánh Giá</th>
                                    <th className="p-4 text-center w-32">Thao Tác</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800/60">
                            {paginatedProducts.length > 0 ? (
                                paginatedProducts.map((item,index) => (
                                    <tr key={item.id} className="hover:bg-slate-800/30 transition-colors">
                                        <td className="p-4 text-center font-mono text-slate-400">
                                            {currentPage * pageSize + index + 1}
                                        </td>
                                        <td className="p-4 text-center">
                                            {item.thumbnail ? (
                                                <img
                                                    src={item.thumbnail}
                                                    alt={item.name}
                                                    className="w-11 h-11 object-cover rounded-xl mx-auto border border-slate-700/80 shadow-sm"
                                                />
                                            ) : (
                                                <div className="w-11 h-11 bg-slate-800 rounded-xl flex items-center justify-center text-slate-500 mx-auto border border-slate-700">
                                                    <ImageIcon className="w-4 h-4" />
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4 font-bold text-white max-w-xs truncate" title={item.name}>
                                            {item.name}
                                        </td>
                                        <td className="p-4 font-mono font-bold text-indigo-400">
                                            {item.price?.toLocaleString()} VNĐ
                                        </td>
                                        <td className="p-4 text-center font-mono font-semibold">
                                            <span className={`px-2.5 py-1 rounded-lg ${item.stock <= 0 ? 'bg-rose-500/10 text-rose-400' : 'bg-slate-800 text-slate-200'}`}>
                                                {item.stock ?? 0}
                                            </span>
                                        </td>
                                        <td className="p-4 max-w-xs truncate text-slate-400" title={item.description}>
                                            {item.description || "---"}
                                        </td>
                                        <td className="p-4 text-slate-300 font-medium max-w-[140px] truncate" title={item.categoryName}>
                                            {item.categoryName || "Chưa phân loại"}
                                        </td>
                                        <td className="p-4 text-center font-mono">
                                            {item.stock === 0 || item.status === 'OUT_OF_STOCK' ? (
                                                <span className="inline-flex items-center gap-1 bg-rose-500/10 text-rose-400 border border-rose-500/20 px-2.5 py-1 rounded-full text-[10px] font-bold">
                                                    <AlertCircle className="w-3 h-3" /> HẾT HÀNG
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2.5 py-1 rounded-full text-[10px] font-bold">
                                                    <CheckCircle2 className="w-3 h-3" /> CÒN HÀNG
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-4 text-center text-amber-400 font-bold flex items-center justify-center gap-1 pt-7">
                                            <Star className="w-3.5 h-3.5 fill-amber-400" /> {item.star || 5}
                                        </td>
                                        <td className="p-4 text-center space-x-1.5">
                                            <button
                                                onClick={() => handleEditClick(item)}
                                                className="p-2.5 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 rounded-2xl transition cursor-pointer"
                                                title="Chỉnh sửa sản phẩm"
                                            >
                                                <Edit3 className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(item.realId || item.id)}
                                                className="p-2.5 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 rounded-2xl transition cursor-pointer"
                                                title="Xóa sản phẩm"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="10" className="p-12 text-center text-slate-500">
                                        Không tìm thấy sản phẩm nào phù hợp.
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* THANH PHÂN TRANG ĐỒNG BỘ */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between p-5 border-t border-slate-800 bg-slate-950/40">
                        <span className="text-xs text-slate-400 font-medium">
                            Trang <span className="font-bold text-white">{currentPage + 1}</span> / <span className="font-bold text-white">{totalPages}</span>
                        </span>

                        <div className="flex items-center space-x-1.5">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 0}
                                className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition ${
                                    currentPage === 0
                                        ? 'border-slate-800 text-slate-600 cursor-not-allowed'
                                        : 'border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white cursor-pointer'
                                }`}
                            >
                                Trước
                            </button>

                            {[...Array(totalPages)].map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => handlePageChange(index)}
                                    className={`w-9 h-9 rounded-xl text-xs font-bold border transition ${
                                        currentPage === index
                                            ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-600/30'
                                            : 'border-slate-700/80 text-slate-300 hover:bg-slate-800 hover:text-white cursor-pointer'
                                    }`}
                                >
                                    {index + 1}
                                </button>
                            ))}

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages - 1}
                                className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition ${
                                    currentPage === totalPages - 1
                                        ? 'border-slate-800 text-slate-600 cursor-not-allowed'
                                        : 'border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white cursor-pointer'
                                }`}
                            >
                                Sau
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* MODAL THÊM SẢN PHẨM */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 z-50 overflow-y-auto">
                    <div className="bg-slate-900 border border-slate-800 text-white p-8 rounded-3xl w-full max-w-xl shadow-2xl my-8 space-y-6">
                        <div className="flex justify-between items-center border-b border-slate-800 pb-4">
                            <h2 className="text-xl font-bold font-serif text-white">Thêm Sản Phẩm Mới</h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white text-lg font-bold cursor-pointer">✕</button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Tên sản phẩm</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Nhập tên sản phẩm chuẩn Nhật..."
                                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-indigo-500 transition"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Danh mục sản phẩm</label>
                                <select
                                    name="categoryId"
                                    value={formData.categoryId}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-indigo-500 transition cursor-pointer"
                                >
                                    <option value="">-- Chọn danh mục --</option>
                                    {categories.map((cat) => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Hình ảnh sản phẩm</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageFileChange}
                                    className="w-full text-xs text-slate-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-slate-800 file:text-indigo-400 hover:file:bg-slate-700 cursor-pointer border border-slate-800 rounded-2xl bg-slate-950 p-2"
                                />
                                {previewUrl && (
                                    <div className="mt-3 flex items-center space-x-3 bg-slate-950 p-3 rounded-2xl border border-slate-800">
                                        <img src={previewUrl} alt="Preview" className="w-12 h-12 object-cover rounded-xl border border-slate-700" />
                                        <span className="text-xs text-slate-300 font-medium">Đã tải ảnh lên thành công</span>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Giá bán (VNĐ)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        name="price"
                                        value={formData.price}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="0"
                                        className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-indigo-500 transition"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Tồn kho</label>
                                    <input
                                        type="number"
                                        min="0"
                                        name="stock"
                                        value={formData.stock}
                                        onChange={handleInputChange}
                                        placeholder="0"
                                        className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-indigo-500 transition"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Đánh giá sao</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        min="1"
                                        max="5"
                                        name="star"
                                        value={formData.star}
                                        onChange={handleInputChange}
                                        placeholder="5.0"
                                        className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-indigo-500 transition"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Mô tả chi tiết</label>
                                <textarea
                                    name="description"
                                    rows="3"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    placeholder="Nhập thông tin chi tiết về sản phẩm..."
                                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-indigo-500 transition resize-none"
                                />
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-5 py-2.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer transition"
                                >
                                    Hủy bỏ
                                </button>
                                <button
                                    type="submit"
                                    className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2.5 rounded-xl text-xs font-bold transition shadow-lg shadow-indigo-600/30 cursor-pointer"
                                >
                                    Lưu sản phẩm
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL CHỈNH SỬA SẢN PHẨM */}
            {isEditModalOpen && (
                <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 z-50 overflow-y-auto">
                    <div className="bg-slate-900 border border-slate-800 text-white p-8 rounded-3xl w-full max-w-xl shadow-2xl my-8 space-y-6">
                        <div className="flex justify-between items-center border-b border-slate-800 pb-4">
                            <h2 className="text-xl font-bold font-serif text-amber-400">
                                Chỉnh Sửa Sản Phẩm (ID: #{editingProduct?.realId || editingProduct?.id})
                            </h2>
                            <button onClick={() => setIsEditModalOpen(false)} className="text-slate-400 hover:text-white text-lg font-bold cursor-pointer">✕</button>
                        </div>

                        <form onSubmit={handleUpdateSubmit} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Tên sản phẩm</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-amber-500 transition"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Danh mục sản phẩm</label>
                                <select
                                    name="categoryId"
                                    value={formData.categoryId}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-amber-500 transition cursor-pointer"
                                >
                                    <option value="">-- Chọn danh mục --</option>
                                    {categories.map((cat) => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Thay đổi hình ảnh</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageFileChange}
                                    className="w-full text-xs text-slate-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-slate-800 file:text-amber-400 hover:file:bg-slate-700 cursor-pointer border border-slate-800 rounded-2xl bg-slate-950 p-2"
                                />
                                {previewUrl && (
                                    <div className="mt-3 flex items-center space-x-3 bg-slate-950 p-3 rounded-2xl border border-slate-800">
                                        <img src={previewUrl} alt="Preview" className="w-12 h-12 object-cover rounded-xl border border-slate-700" />
                                        <span className="text-xs text-slate-300 font-medium">Hình ảnh hiện tại hoặc mới chọn</span>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Giá bán (VNĐ)</label>
                                    <input
                                        type="number"
                                        min="0"
                                    name="price"
                                        value={formData.price}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-amber-500 transition"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Tồn kho</label>
                                    <input
                                        type="number"
                                        min="0"
                                        name="stock"
                                        value={formData.stock}
                                        onChange={handleInputChange}
                                        className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-amber-500 transition"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Đánh giá sao</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        min="1"
                                        max="5"
                                        name="star"
                                        value={formData.star}
                                        onChange={handleInputChange}
                                        className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-amber-500 transition"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-300 mb-1.5 uppercase tracking-wider">Mô tả chi tiết</label>
                                <textarea
                                    name="description"
                                    rows="3"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-3.5 text-xs text-white focus:outline-none focus:border-amber-500 transition resize-none"
                                />
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="px-5 py-2.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer transition"
                                >
                                    Hủy bỏ
                                </button>
                                <button
                                    type="submit"
                                    className="bg-amber-600 hover:bg-amber-500 text-white px-6 py-2.5 rounded-xl text-xs font-bold transition shadow-lg shadow-amber-600/30 cursor-pointer"
                                >
                                    Cập nhật thay đổi
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}